import moment from "moment";

import { getAccountIdentifier } from "../authProvider";
import * as actionTypes from "../constants";
import {
  getGeneralPreferenceActionCompleted,
  getGeneralPreferenceActionError,
} from "../scenes/SystemPreferences/actions/generalPreferenceAction";
import { getCurrentUserOrganizationComplete } from "../scenes/SystemPreferences/actions/organizationAction";
import {
  getRemoteServicesSettingsActionCompleted,
  getRemoteServicesSettingsActionError,
} from "../scenes/SystemPreferences/actions/remoteServicesSettingsAction";
import { getAnnouncements } from "../services/announcements";
import { currentUserAPI } from "../services/currentUser";
import { parseServiceErrors } from "../services/encounter/serviceErrors";
import { systemPreferencesAPI } from "../services/systemPreferences";
import { userPreferencesAPI } from "../services/userPreferences";
import { userAPI } from "../services/users";

import { getAnnouncementsCompleted, getAnnouncementsError } from "./announcements";
import { addErrorsAction } from "./errors";
import {
  initColumnsSettingsAction,
  initShowColumnsReorderWarningAction,
  initShowColumnsResizeWarningAction,
} from "./gridColumnsSettings";

import type { CurrentUserEntity } from "../models";

// using Dispatch type here causes error, not sure why
export const initializeAction = () => (dispatch) => {
  dispatch(initializeStart());
  dispatch(getUserPreferencesBegin());
  dispatch(getCurrentUserBegin());

  const initCalls: Promise<any>[] = [];

  // announcements
  initCalls.push(
    getAnnouncements()
      .then((announcements) => {
        dispatch(getAnnouncementsCompleted(announcements));
      })
      .catch(() => {
        dispatch(
          getAnnouncementsError([
            {
              description: "Error Retrieving Announcements",
              error: "Error Retrieving Announcements",
              time: moment.now(),
            },
          ]),
        );
      }),
  );

  // User preferences
  initCalls.push(
    userPreferencesAPI
      .getUserPreferences()
      .then((preferences) => {
        dispatch(getUserPreferencesCompleted(preferences));
      })
      .catch((reason) => {
        const errors = parseServiceErrors(reason, "Error Retrieving User Preferences");
        dispatch(addErrorsAction(errors));
        dispatch(getUserPreferencesError("Error Retrieving User Preferences"));
        throw new Error("User preferences fetch error");
      }),
  );

  // User account
  initCalls.push(
    userAPI
      .getUser(getAccountIdentifier())
      .then((getUserResult) => {
        dispatch(getUserAccountCompleted(getUserResult));
      })
      .catch((reason) => {
        const errors = parseServiceErrors(reason, "Error Retrieving User Account");
        dispatch(addErrorsAction(errors));
        dispatch(getUserPreferencesError("Error Retrieving User Account"));
        throw new Error("User account fetch error");
      }),
  );

  // User permissions - fetch this first so we can check user type
  initCalls.push(
    currentUserAPI
      .getCurrentUser({ includeRoles: false })
      .then((user) => {
        dispatch(getCurrentUserCompleted(user));
        return user; // Return user for the next promise
      })
      .catch((reason) => {
        const errors = parseServiceErrors(reason, "Error Retrieving Current User");
        dispatch(addErrorsAction(errors));
        dispatch(getCurrentUserError("Error Retrieving Current User"));
        throw new Error("Current user fetch error");
      }),
  );

  initCalls.push(
    systemPreferencesAPI
      .getGeneralPreferences()
      .then((generalPreferences) => {
        dispatch(getGeneralPreferenceActionCompleted(generalPreferences));
      })
      .catch((reason) => {
        const errors = parseServiceErrors(reason, "Error Retrieving General Preferences");
        dispatch(addErrorsAction(errors));
        dispatch(getGeneralPreferenceActionError(reason));
      }),
  );

  initCalls.push(
    systemPreferencesAPI
      .getRemoteServicesSettings()
      .then((settings) => {
        dispatch(getRemoteServicesSettingsActionCompleted(settings));
      })
      .catch((reason) => {
        const errors = parseServiceErrors(reason, "Error Retrieving Remote Services Settings");
        dispatch(addErrorsAction(errors));
        dispatch(getRemoteServicesSettingsActionError(reason));
      }),
  );

  initCalls.push(
    systemPreferencesAPI
      .getCurrentOrganization()
      .then((organization) => {
        tenantId = organization.ViewId;
        dispatch(getCurrentUserOrganizationComplete(organization || ""));
      })
      .catch((reason) => {
        const errors = parseServiceErrors(reason, "Error Retrieving Organization Name");
        dispatch(addErrorsAction(errors));
        dispatch(getGeneralPreferenceActionError(reason));
      }),
  );

  /*
  let loadedFacilities: IdDescriptionBase[] = [];
  // TODO: stop fetching facilities until necessary
  // application loading sequence. I want to have single flag (initializing) to indicate loading
  initCalls.push(
    choiceListsAPI
      .getFacilities()
      .then((facilities) => {
        loadedFacilities = facilities;
        dispatch(getFacilitiesCompleted(facilities));
        if (!loadedFacilities || loadedFacilities.length === 0) {
          dispatch(getFacilitiesError('No Facilities'));
        }
      })
      .catch(() => {
        dispatch(getFacilitiesError('Facilities fetch error'));
        throw new Error('Facilities fetch error');
      })
  );
  */

  let tenantId: string | undefined;

  Promise.all(initCalls)
    .then(() => {
      dispatch(initColumnsSettingsAction());
      dispatch(initShowColumnsReorderWarningAction());
      dispatch(initShowColumnsResizeWarningAction());
      dispatch(initializeCompleted());

      const previousTenant = localStorage.getItem("previousTenant");
      localStorage.setItem("previousTenant", tenantId || "");

      // navigate to home page is tenant id is different from before
      if (previousTenant && previousTenant !== tenantId && window.location.pathname !== "/") {
        window.location.assign("/");
      }
    })
    .catch((reason: Error) => {
      dispatch(
        initializeError({ errorReason: "Error while initializing", errorDetail: reason.message }),
      );
    });
};

const initializeStart = () => ({
  type: actionTypes.INITIALIZE_BEGIN,
});

const initializeCompleted = () => ({
  payload: "",
  type: actionTypes.INITIALIZE_COMPLETED,
});

const initializeError = (error: { errorReason: string; errorDetail: string }) => ({
  payload: error,
  type: actionTypes.INITIALIZE_FAIL,
});

const getUserPreferencesBegin = () => ({
  type: actionTypes.GET_USER_PREFERENCES_BEGIN,
});

const getUserPreferencesCompleted = (preferences: object) => ({
  payload: preferences,
  type: actionTypes.GET_USER_PREFERENCES_COMPLETED,
});

const getUserPreferencesError = (error: string) => ({
  payload: error,
  type: actionTypes.GET_USER_PREFERENCES_ERROR,
});

const getCurrentUserCompleted = (user: CurrentUserEntity) => ({
  payload: user,
  type: actionTypes.GET_CURRENT_USER_COMPLETED,
});

const getCurrentUserError = (error: string) => ({
  payload: error,
  type: actionTypes.GET_CURRENT_USER_ERROR,
});

const getCurrentUserBegin = () => ({
  type: actionTypes.GET_CURRENT_USER_BEGIN,
});

const getUserAccountCompleted = (user: any) => ({
  payload: user,
  type: actionTypes.GET_USER_COMPLETED,
});
